import { useCallback, useEffect, useRef, useState } from 'react';
import { useMusicState } from './storeHooks/useMusicState';
import useAlbumData from './useAlbumData';

interface UseMusicInfiniteScrollOptions {
  rootMargin?: string;
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  enabled?: boolean;
  genre?: string;
  search?: string;
}

interface UseMusicInfiniteScrollReturn {
  loading: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
  reset: () => void;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  albums: any[]; // Using the store data
}

/**
 * Custom hook for managing infinite scroll of music albums using the Redux store
 * This ensures both MusicForYou and GridPosts components share the same data
 */
export const useMusicInfiniteScroll = (
  options: UseMusicInfiniteScrollOptions = {},
): UseMusicInfiniteScrollReturn => {
  const {
    rootMargin = '200px',
    scrollContainerRef,
    enabled = true,
    genre,
    search,
  } = options;

  // Get store state and actions
  const {
    musicforyouList: albums,
    appendMusicForYouList,
    setMusicForYouList,
  } = useMusicState();

  const { loadMusicAlbumsPaginated } = useAlbumData();

  // Local state for infinite scroll
  // Initialize loading to true if no albums exist to show skeleton during initial load
  const [loading, setLoading] = useState(albums.length === 0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refs for managing state and observer
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const initialLoadTriggered = useRef(false);

  // Load more function
  const loadMore = useCallback(async () => {
    console.log('loadMore called:', {
      loading: loadingRef.current,
      hasMore,
      enabled,
      offset: offsetRef.current,
      genre,
    });

    if (loadingRef.current || !hasMore || !enabled) {
      console.log('loadMore early return:', {
        loading: loadingRef.current,
        hasMore,
        enabled,
      });
      return;
    }

    console.log('Starting API call with genre:', genre);
    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await loadMusicAlbumsPaginated(
        12,
        offsetRef.current,
        genre,
        search,
      );
      // console.log('API result:', { result, genre, search, offset: offsetRef.current });

      if (offsetRef.current === 0) {
        // Initial load - replace items
        setMusicForYouList(result?.items || []);
      } else {
        // Subsequent loads - append items
        appendMusicForYouList(result?.items || []);
      }

      const hasNext = result?.hasNext || false;
      setHasMore(hasNext);
      offsetRef.current += result?.items?.length || 0;

      // Set initial load done after first successful load
      if (!initialLoadDone) {
        setInitialLoadDone(true);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load more items';
      setError(errorMessage);
      console.error('Error loading more items:', err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [
    loadMusicAlbumsPaginated,
    hasMore,
    enabled,
    appendMusicForYouList,
    setMusicForYouList,
    initialLoadDone,
    // Note: genre is not in dependencies to avoid recreating loadMore on every genre change
    // genre is captured in the closure when loadMore is called
  ]);

  // Reset function
  const reset = useCallback(() => {
    setMusicForYouList([]);
    offsetRef.current = 0;
    setHasMore(true);
    setError(null);
    setLoading(false);
    loadingRef.current = false;
    setInitialLoadDone(false);
    initialLoadTriggered.current = false;
  }, [setMusicForYouList]);

  // Reset data when genre or search changes and trigger new load
  useEffect(() => {
    console.log(
      'Music genre/search changed, resetting data and triggering load:',
      { genre, search },
    );

    // Set loading state immediately to prevent showing old data
    setLoading(true);
    loadingRef.current = true;

    setMusicForYouList([]);
    offsetRef.current = 0;
    setHasMore(true);
    setError(null);
    setInitialLoadDone(false);
    initialLoadTriggered.current = false;

    if (enabled) {
      const triggerLoad = async () => {
        try {
          const result = await loadMusicAlbumsPaginated(12, 0, genre, search);
          console.log('API result for genre/search change:', {
            result,
            genre,
            search,
          });

          setMusicForYouList(result?.items || []);
          const hasNext = result?.hasNext || false;
          setHasMore(hasNext);
          offsetRef.current = (result?.items || []).length;
          setInitialLoadDone(true);
        } catch (err) {
          console.error('Error loading albums for genre/search:', err);
          setError(
            err instanceof Error ? err.message : 'Failed to load albums',
          );
          setHasMore(false);
        } finally {
          setLoading(false);
          loadingRef.current = false;
        }
      };

      triggerLoad();
    } else {
      console.log('Not triggering loadMore - enabled is false');
      // Reset loading state even when disabled
      setLoading(false);
      loadingRef.current = false;
    }
  }, [genre, search, enabled, loadMusicAlbumsPaginated]);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (!enabled || !hasMore || !sentinelRef.current) return;

    // Disconnect any existing observer first
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Determine the root element for intersection observer
    // If scrollContainerRef is provided, use it; otherwise use null (viewport)
    const rootElement = scrollContainerRef?.current || null;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !loadingRef.current && hasMore) {
          loadMore();
        }
      },
      {
        root: rootElement,
        rootMargin,
        threshold: 0.1,
      },
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    enabled,
    hasMore,
    initialLoadDone,
    loadMore,
    rootMargin,
    scrollContainerRef,
  ]);

  // Initial load effect
  useEffect(() => {
    if (!enabled || initialLoadTriggered.current) return;
    if (!initialLoadDone && albums.length === 0) {
      initialLoadTriggered.current = true;
      // Set loading to true immediately for initial load to show skeleton
      setLoading(true);
      loadingRef.current = true;
      loadMore(); // loadMore will set initialLoadDone after completion
    } else if (albums.length > 0 && !initialLoadDone) {
      // If albums already exist in store, sync the offset
      initialLoadTriggered.current = true;
      setInitialLoadDone(true);
      offsetRef.current = albums.length;
    }
  }, []);

  return {
    loading,
    hasMore,
    error,
    loadMore,
    reset,
    sentinelRef,
    albums,
  };
};

export default useMusicInfiniteScroll;
